// TelcoTrack — D5-RAYTR3 occupancy graph + Czech table (stojan / ODF / pozice).

import { getRayOfsmModule, rayOfsmTraysFor } from './rozvadec-ofsm.constants';
import { occupancySplitterPortLabel } from './rozvadec-occupancy-schema.ids';
import type {
  RozvadecFiberEndpoint,
  RozvadecOccupancyGenerateInput,
  RozvadecOccupancySchema,
  RozvadecOccupancyServiceAssignment,
  RozvadecOccupancySplitter,
} from './rozvadec-occupancy-schema.types';
import { formatRsuD5Address, rsuStatusLabelCs } from './rozvadec-rsu-occupancy';
import { emptyRsuOccupancy } from './rozvadec-rsu-occupancy';
import type { RsuOccupancy, RsuTermination } from './rozvadec-rsu-occupancy.types';
import { buildRozvadecOccupancySheets, inferRozvadecNetworkRole } from './rozvadec-occupancy-schema.sheets';

export const ROZVADEC_RAYTR3_D5_HEADERS = [
  'Pořadí ODF',
  'Typ ODF',
  'SAP modulu',
  'Pozice',
  'Typ konektoru',
  'Název OK',
  'Fyzické vlákno',
  'Logické vlákno',
  'Směr/cíl',
  'Stav',
] as const;

export type RozvadecRayTr3D5Row = {
  odfOrder: number;
  odfType: string;
  moduleSap: string;
  position: number | string;
  connectorType: string;
  cableName: string;
  physicalFiber: string;
  logicalFiber: string;
  destination: string;
  status: string;
  address: string;
};

export function buildRayTr3OccupancySchema(
  occupancy: RsuOccupancy | null | undefined,
  input: RozvadecOccupancyGenerateInput = {},
): RozvadecOccupancySchema {
  const payload = occupancy ?? emptyRsuOccupancy();
  const stojan = (input.stojanLabel ?? input.cabinetPrintName ?? 'RAY-TR3').trim() || 'RAY-TR3';
  const fibers = new Map<string, RozvadecFiberEndpoint>();
  const assignments: RozvadecOccupancyServiceAssignment[] = [];
  const splitters: RozvadecOccupancySplitter[] = [];

  for (const term of payload.terminations) {
    if (!term.cableId || term.physicalFiberNumber == null) continue;
    const key = `${term.cableId}#${term.physicalFiberNumber}`;
    if (!fibers.has(key)) {
      fibers.set(key, {
        id: `fiber:${key}`,
        cableId: term.cableId,
        physicalFiber: term.physicalFiberNumber,
        logicalFiber: term.logicalFiberNumber,
        firstFeederCableId: null,
        role: term.status === 'reserve' ? 'reserve' : 'feeder',
      });
    }
    if (term.destinationObjectId) {
      assignments.push({
        id: `asg:${term.id}`,
        buildingId: term.destinationObjectId,
        cableId: term.cableId,
        physicalFiber: term.physicalFiberNumber,
        logicalFiber: term.logicalFiberNumber,
        sourcePhysicalFiber: term.physicalFiberNumber,
        via: 'connector',
        connectorId: `RAYTR3:ODF${term.moduleId}:P${term.connectorPosition}`,
        splitterPortId: null,
        spliceId: `RAYTR3:ODF${term.moduleId}:T${term.trayNumber}:S${term.trayWeldPosition}`,
      });
    }
  }

  for (const module of payload.modules) {
    const def = getRayOfsmModule(module.materialSap);
    if (!def || def.kind !== 'splitter') continue;
    const ratio = def.splitterRatio ?? '1:8';
    const outputCount = ratio === '1:16' ? 16 : 8;
    for (let index = 1; index <= def.splitterCount; index += 1) {
      const id = `rsu-spl:${module.id}:${index}`;
      const path = payload.splitterPaths.find(
        (item) => item.moduleId === module.id && item.splitterIndex === index,
      );
      splitters.push({
        id,
        printLabel: `Splitter ${index}`,
        cassetteId: module.id,
        ratio,
        splitterIndex: index,
        insideCassette: true,
        inputs: [
          {
            id: `${id}:in:1`,
            splitterId: id,
            direction: 'in',
            splitterIndex: index,
            port: 1,
            printLabel: occupancySplitterPortLabel({ splitterIndex: index, direction: 'in', port: 1 }),
            rowPosition: null,
            incoming: null,
            target: null,
          },
        ],
        outputs: Array.from({ length: outputCount }, (_, out) => ({
          id: `${id}:out:${out + 1}`,
          splitterId: id,
          direction: 'out' as const,
          splitterIndex: index,
          port: out + 1,
          printLabel: occupancySplitterPortLabel({ splitterIndex: index, direction: 'out', port: out + 1 }),
          rowPosition: null,
          incoming: null,
          target: null,
        })),
      });
      void path;
    }
  }

  const schema: RozvadecOccupancySchema = {
    version: 1,
    rendererId: 'D5-RAYTR3',
    cabinetCode: 'RAYTR3',
    typeId: 'RSU_ODF',
    sapCode: input.derivedFromSap ?? '325715',
    printName: input.cabinetPrintName ?? 'RSU / RAY-TR3',
    networkRole: inferRozvadecNetworkRole(input, 'RSU_ODF'),
    locality: input.locality ?? null,
    objectName: input.objectName ?? input.cabinetPrintName ?? null,
    configuration: 'RAY-TR3',
    sheets: [],
    rows: [
      {
        id: 'rack',
        label: `RAY-TR3 42U — ${stojan}`,
        positions: 42,
        fillFrom: 'top',
      },
    ],
    graph: {
      fibers: [...fibers.values()],
      cassettes: payload.modules.map((module) => {
        const def = getRayOfsmModule(module.materialSap);
        return {
          id: module.id,
          cassetteType: 'KM4',
          printLabel: def?.d5TypeLabel ?? module.materialSap,
          rowId: 'rack',
          rowLabel: `ODF ${module.odfOrder}`,
          startPosition: module.rackStartU,
          displayPosition: module.odfOrder,
          occupiedPositions: Array.from({ length: module.heightU }, (_, index) => module.rackStartU + index),
          adapterFamily: def?.connectorType === 'LC/APC' ? 'LC' : def?.connectorType ? 'SC' : null,
        };
      }),
      connectors: payload.terminations.map((term) => ({
        id: `RAYTR3:ODF${term.moduleId}:P${term.connectorPosition}`,
        cassetteId: term.moduleId,
        adapterPosition: term.connectorPosition,
        polarity: null,
        printLabel: String(term.connectorPosition),
        incoming:
          term.cableId && term.physicalFiberNumber != null
            ? { cableId: term.cableId, physicalFiber: term.physicalFiberNumber }
            : null,
        pigtailSpliceId: `RAYTR3:ODF${term.moduleId}:T${term.trayNumber}:S${term.trayWeldPosition}`,
      })),
      splices: payload.terminations.map((term) => ({
        id: `RAYTR3:ODF${term.moduleId}:T${term.trayNumber}:S${term.trayWeldPosition}`,
        cassetteId: term.moduleId,
        seat: term.trayWeldPosition,
        printLabel: `vana ${term.trayNumber} / svar ${term.trayWeldPosition}`,
        kind: 'pigtail' as const,
        fibers:
          term.cableId && term.physicalFiberNumber != null
            ? [{ cableId: term.cableId, physicalFiber: term.physicalFiberNumber }]
            : [],
      })),
      splitters,
      assignments,
    },
    pon32: null,
    rsuOccupancy: payload,
  };
  schema.sheets = buildRozvadecOccupancySheets(schema, input);
  return schema;
}

export function buildRayTr3D5Rows(
  occupancy: RsuOccupancy,
  stojanLabel: string,
): RozvadecRayTr3D5Row[] {
  const rows: RozvadecRayTr3D5Row[] = [];
  for (const module of [...occupancy.modules].sort((a, b) => a.odfOrder - b.odfOrder)) {
    const def = getRayOfsmModule(module.materialSap);
    if (!def) continue;
    if (def.kind === 'splitter') {
      for (let index = 1; index <= def.splitterCount; index += 1) {
        rows.push({
          odfOrder: module.odfOrder,
          odfType: def.d5TypeLabel,
          moduleSap: def.sapCode,
          position: `SPL ${index} IN`,
          connectorType: def.splitterInputConnectorType ?? '—',
          cableName: '—',
          physicalFiber: '—',
          logicalFiber: '—',
          destination: '—',
          status: 'volná',
          address: `${stojanLabel} / ODF ${module.odfOrder} / splitter ${index} IN`,
        });
        const outs = def.splitterRatio === '1:16' ? 16 : 8;
        for (let out = 1; out <= outs; out += 1) {
          const patch = occupancy.patchConnections.find((item) => {
            const ports = [item.source, item.target];
            return ports.some(
              (port) =>
                port.kind === 'splitter-out' &&
                port.moduleId === module.id &&
                port.splitterIndex === index &&
                port.output === out,
            );
          });
          const peer = patch
            ? patch.source.kind === 'splitter-out' && patch.source.output === out
              ? patch.target
              : patch.source
            : null;
          const dest =
            peer?.kind === 'ofsm-connector'
              ? `OFSM ${peer.moduleId} / pozice ${peer.connectorPosition}`
              : peer?.kind === 'cable-fiber'
                ? `vl. ${peer.physicalFiber}`
                : '—';
          rows.push({
            odfOrder: module.odfOrder,
            odfType: def.d5TypeLabel,
            moduleSap: def.sapCode,
            position: `SPL ${index} OUT ${out}`,
            connectorType: def.splitterOutputConnectorType ?? '—',
            cableName: '—',
            physicalFiber: '—',
            logicalFiber: '—',
            destination: dest,
            status: dest === '—' ? 'volná' : 'provoz',
            address: `${stojanLabel} / ODF ${module.odfOrder} / splitter ${index} OUT ${out}`,
          });
        }
      }
      continue;
    }
    const byPos = new Map<number, RsuTermination>();
    for (const term of occupancy.terminations) {
      if (term.moduleId === module.id) byPos.set(term.connectorPosition, term);
    }
    for (const tray of rayOfsmTraysFor(def.sapCode)) {
      for (let position = tray.firstConnector; position <= tray.lastConnector; position += 1) {
        const term = byPos.get(position);
        rows.push({
          odfOrder: module.odfOrder,
          odfType: def.d5TypeLabel,
          moduleSap: def.sapCode,
          position,
          connectorType: term?.connectorType ?? def.connectorType ?? '—',
          cableName: term?.cableId ?? '—',
          physicalFiber: term?.physicalFiberNumber != null ? String(term.physicalFiberNumber) : '—',
          logicalFiber: term?.logicalFiberNumber != null ? String(term.logicalFiberNumber) : '—',
          destination: term?.destinationLabel ?? term?.destinationObjectId ?? '—',
          status: rsuStatusLabelCs(term?.status ?? 'free'),
          address: formatRsuD5Address({
            stojanLabel,
            odfOrder: module.odfOrder,
            connectorPosition: position,
          }),
        });
      }
    }
  }
  return rows;
}

export function rayTr3D5Aoa(rows: readonly RozvadecRayTr3D5Row[], title: string): (string | number)[][] {
  return [
    ['Schéma osazení D5', title],
    ['Renderer', 'D5-RAYTR3'],
    [],
    [...ROZVADEC_RAYTR3_D5_HEADERS],
    ...rows.map((row) => [
      row.odfOrder,
      row.odfType,
      row.moduleSap,
      row.position,
      row.connectorType,
      row.cableName,
      row.physicalFiber,
      row.logicalFiber,
      row.destination,
      row.status,
    ]),
  ];
}
